import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Button, Stack, Alert } from '@mui/material';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const VerifyEmailPage: React.FC = () => {
  const { isAuthenticated, isEmailVerified, resendVerificationEmail, reloadAndSyncUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    // If redirected back after verification (e.g., ?verified=1), reload user
    const verifiedFlag = searchParams.get('verified');
    if (verifiedFlag === '1') {
      reloadAndSyncUser().then(() => {
        setInfo('Thanks! Your email has been verified.');
      }).catch(() => {});
    }
  }, [searchParams, reloadAndSyncUser]);

  const handleResend = async () => {
    setSending(true);
    try {
      await resendVerificationEmail();
      setSent(true);
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ pt: 10, pb: 4 }}>
      <Paper sx={{ p: 3, maxWidth: 560, mx: 'auto' }}>
        <Stack spacing={2}>
          <Typography variant="h5">Verify your email</Typography>
          {!isAuthenticated && (
            <Alert severity="info">You need to be logged in to verify your email. <Link to="/login">Go to login</Link></Alert>
          )}
          {info && <Alert severity="success">{info}</Alert>}
          {isAuthenticated && !isEmailVerified && (
            <>
              <Typography>
                We sent you a verification link. Please click the link in the email to verify your address.
                If you can’t find it, check your spam folder or resend it below.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={handleResend} disabled={sending || sent}>
                  {sent ? 'Verification sent' : (sending ? 'Sending…' : 'Resend verification email')}
                </Button>
                <Button variant="outlined" onClick={() => reloadAndSyncUser()}>
                  I verified, refresh status
                </Button>
              </Stack>
            </>
          )}
          {isAuthenticated && isEmailVerified && (
            <Alert severity="success">Your email is verified. You can continue using all features.</Alert>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default VerifyEmailPage;
